import type { SupabaseClient } from '@supabase/supabase-js'
import type { IcbtProgress, WorkbookEntry } from '@repo/icbt/meta'
import { formatInTz } from '@repo/time'

/**
 * Load an iCBT enrollment's progress snapshot from Supabase.
 *
 * Reads the append-only `workbook_entries` log (student output) +
 * `section_completions` stamps (engagement events) for a single
 * enrollment, and derives the display-ready shape used by every
 * client/therapist workbook renderer.
 *
 * **Auth is the caller's job.** This helper takes an already-authorised
 * admin client and enrollment id — imwell verifies the enrolled
 * student owns the enrollment before calling; the therapist app
 * verifies the therapist owns the episode the enrollment is attached
 * to. Doing ownership here would force one auth model on both apps.
 *
 * Returns `null` when the enrollment doesn't exist. Fetch errors on
 * the two child queries are logged + treated as empty (progress
 * lookup shouldn't lock a user out of their workbook because Supabase
 * had a hiccup).
 *
 * Extracted 2026-09-05 from
 * `apps/imwell/app/dashboard/program/[programSlug]/actions.ts` so the
 * therapist iCBT tab can render the same data without duplicating the
 * query.
 */
export async function getEnrollmentProgress(opts: {
  adminClient: SupabaseClient
  enrollmentId: string
  /**
   * IANA timezone for the streak calculation. Streak counts consecutive
   * days of activity in this timezone (UTC bucketing would mis-credit
   * students whose local day rolls over outside UTC). Defaults to UTC
   * when omitted.
   */
  timezone?: string | null
}): Promise<{
  enrollment: { id: string; userId: string; programId: string } | null
  progress: IcbtProgress | null
}> {
  const { adminClient, enrollmentId, timezone } = opts

  const { data: enrollment } = await adminClient
    .from('program_enrollments')
    .select('id, "userId", "programId"')
    .eq('id', enrollmentId)
    .maybeSingle()
  if (!enrollment) return { enrollment: null, progress: null }

  const [entriesRes, completionsRes] = await Promise.all([
    adminClient
      .from('workbook_entries')
      .select('id, kind, "moduleSlug", "sectionSlug", "subKey", data, "revisionOf", "createdAt"')
      .eq('enrollmentId', enrollmentId)
      .order('createdAt', { ascending: false }),
    adminClient
      .from('section_completions')
      .select('"moduleSlug", "sectionSlug", "completedAt"')
      .eq('enrollmentId', enrollmentId),
  ])
  if (entriesRes.error) {
    console.warn('[icbt-progress] entries err:', entriesRes.error.message)
    return {
      enrollment: {
        id: enrollment.id as string,
        userId: enrollment.userId as string,
        programId: enrollment.programId as string,
      },
      progress: null,
    }
  }
  if (completionsRes.error) {
    console.warn('[icbt-progress] completions err:', completionsRes.error.message)
    return {
      enrollment: {
        id: enrollment.id as string,
        userId: enrollment.userId as string,
        programId: enrollment.programId as string,
      },
      progress: null,
    }
  }

  const entries: WorkbookEntry[] = (entriesRes.data ?? []).map((r) => ({
    id: r.id as string,
    kind: r.kind as string,
    moduleSlug: (r.moduleSlug as string | null) ?? null,
    sectionSlug: (r.sectionSlug as string | null) ?? null,
    subKey: (r.subKey as string | null) ?? null,
    data: (r.data as Record<string, unknown>) ?? {},
    revisionOf: (r.revisionOf as string | null) ?? null,
    createdAt: r.createdAt as string,
  }))

  const completedSections = (completionsRes.data ?? []).map(
    (r) => `${r.moduleSlug}:${r.sectionSlug}`,
  )

  const tz = timezone ?? undefined
  const activityDays = new Set<string>()
  let firstAt: string | undefined
  let lastAt: string | undefined
  const stamp = (t: string) => {
    if (!firstAt || t < firstAt) firstAt = t
    if (!lastAt || t > lastAt) lastAt = t
    activityDays.add(ymdInTz(parsePgTimestamp(t), tz))
  }
  for (const e of entries) stamp(e.createdAt)
  for (const c of completionsRes.data ?? []) stamp(c.completedAt as string)

  // Streak: consecutive days descending from "today" in the caller's tz.
  const today = ymdInTz(new Date(), tz)
  let streakDays = 0
  if (activityDays.has(today)) {
    streakDays = 1
    let cursor = today
    // Safety cap so a corrupt activityDays set (should be impossible)
    // can't spin the loop forever.
    for (let i = 0; i < 3650; i++) {
      cursor = prevYmdInTz(cursor, tz)
      if (activityDays.has(cursor)) streakDays += 1
      else break
    }
  }

  const firstVisitedAt = firstAt ? ymdInTz(parsePgTimestamp(firstAt), tz) : undefined
  const lastVisitedAt = lastAt ? ymdInTz(parsePgTimestamp(lastAt), tz) : undefined

  return {
    enrollment: {
      id: enrollment.id as string,
      userId: enrollment.userId as string,
      programId: enrollment.programId as string,
    },
    progress: {
      completedSections,
      entries,
      firstVisitedAt,
      lastVisitedAt,
      streakDays,
    },
  }
}

/**
 * Format a date as YYYY-MM-DD in the given IANA timezone. Falls back
 * to the system timezone when tz is undefined/invalid.
 *
 * Inlined from `apps/imwell/lib/dashboard/checkinStats.ts` — duplicated
 * here so this shared service doesn't pull an imwell-only dep. If a
 * third caller needs it, promote to a shared `packages/supabase/utils`.
 */
function ymdInTz(date: Date, tz: string | undefined): string {
  try {
    return formatInTz(date, tz || 'UTC', 'yyyy-MM-dd')
  } catch {
    return date.toISOString().slice(0, 10)
  }
}

/**
 * Postgres `timestamp without time zone` comes back from PostgREST as
 * naive strings like "2026-06-02T05:54:25.67". Native `new Date()`
 * interprets those as LOCAL time; we store UTC, so re-parse as UTC.
 */
function parsePgTimestamp(ts: string | Date): Date {
  if (ts instanceof Date) return ts
  if (/Z$|[+-]\d{2}:?\d{2}$/.test(ts)) return new Date(ts)
  return new Date(ts.replace(' ', 'T') + 'Z')
}

/** Decrement a YYYY-MM-DD wall-clock day in the given timezone.
 *  Anchors on noon UTC so DST spring-forward / fall-back can't shift
 *  the result into the wrong wall-clock day. */
function prevYmdInTz(ymd: string, tz: string | undefined): string {
  const d = new Date(`${ymd}T12:00:00Z`)
  d.setUTCDate(d.getUTCDate() - 1)
  return ymdInTz(d, tz)
}
